import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import { useMemo, useState } from 'react';
import { ProductType, useDebouncedProductsLazy } from '@travelity/api';
import type { ProductMinimal } from '@travelity/api';

import { InfoOutlined } from '@mui/icons-material';
import { getUnixTime } from 'date-fns';
import { getScheduleRecurrenceLabel } from '../../../../helpers';

import { hasCommonAvailableDay } from './helpers';

export type AddProductModalProps = {
    open: boolean;
    dayLabel: string;
    selectedProducts: ProductMinimal[];
    globalCurrencyAbbr?: string;
    onAdd: (product: ProductMinimal) => void;
    onClose: () => void;
};

const AddProductModal = ({
    open,
    dayLabel,
    selectedProducts,
    globalCurrencyAbbr,
    onAdd,
    onClose,
}: AddProductModalProps) => {
    const [search, setSearch] = useState('');
    const availableFromTs = useMemo(() => getUnixTime(new Date()), []);
    const {
        data: products,
        isLoading,
        hasNextPage,
        fetchNextPage,
    } = useDebouncedProductsLazy({
        search,
        types: [ProductType.TOUR, ProductType.TRANSFER],
        resolveSchedule: true,
        referencesOnly: false,
        availableFromTs,
        pageSize: 5,
    });

    const selectedIds = useMemo(
        () => new Set(selectedProducts.map(product => product.id)),
        [selectedProducts]
    );

    const isProductEligible = (candidate: ProductMinimal): boolean => {
        if (selectedProducts.length === 0) return true;
        return hasCommonAvailableDay([...selectedProducts, candidate]);
    };

    const isProductCurrencyEligible = (candidate: ProductMinimal): boolean => {
        if (!globalCurrencyAbbr) return true;
        return candidate.currency?.abbr === globalCurrencyAbbr;
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Add product for {dayLabel}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1, mb: 2 }}>
                    <TextField
                        label="Search product"
                        placeholder="Type product name"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        fullWidth
                    />
                </Box>
                {selectedProducts.length > 0 && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, display: 'flex', alignItems: 'center' }}
                    >
                        <InfoOutlined />
                        Additional products must be available on the same day as
                        the selected products.
                    </Typography>
                )}
                <TableContainer sx={{ border: 'none' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Product
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Schedule
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Duration (h)
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Autoscale
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Loading products...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!isLoading && !products?.length && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No products found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {products?.map(product => {
                                const resolvedSchedule =
                                    product.resolvedSchedules?.[0];
                                const scheduleLabel = resolvedSchedule
                                    ? getScheduleRecurrenceLabel(
                                          resolvedSchedule.recurrence,
                                          resolvedSchedule.dates
                                      )
                                    : 'No schedules';
                                const durationHours =
                                    product.schedule?.[0]?.duration?.estimated
                                        ?.hours ?? '-';
                                const isAdded = selectedIds.has(product.id);
                                const isEligible = isProductEligible(product);
                                const isCurrencyEligible =
                                    isProductCurrencyEligible(product);
                                return (
                                    <TableRow key={product.id} hover>
                                        <TableCell>
                                            <Typography fontWeight={600}>
                                                {product.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textTransform: 'capitalize',
                                                }}
                                            >
                                                {product.type}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            sx={{ textTransform: 'capitalize' }}
                                        >
                                            <Box>
                                                <Typography variant="body2">
                                                    {scheduleLabel}
                                                </Typography>
                                                {!isEligible && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                    >
                                                        Selected products must
                                                        share at least one
                                                        common available day
                                                    </Typography>
                                                )}
                                                {!isCurrencyEligible && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                    >
                                                        Product currency must be{' '}
                                                        {globalCurrencyAbbr}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{durationHours}</TableCell>
                                        <TableCell>
                                            {product.isAutoscale ? 'Yes' : 'No'}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={
                                                    isAdded ||
                                                    !isEligible ||
                                                    !isCurrencyEligible
                                                }
                                                onClick={() => {
                                                    if (
                                                        selectedProducts.length >
                                                            0 &&
                                                        !isProductEligible(
                                                            product
                                                        )
                                                    ) {
                                                        return;
                                                    }
                                                    if (
                                                        !isProductCurrencyEligible(
                                                            product
                                                        )
                                                    ) {
                                                        return;
                                                    }
                                                    onAdd(product);
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {hasNextPage && (
                    <Button
                        variant="text"
                        sx={{ mt: 2 }}
                        onClick={() => fetchNextPage()}
                    >
                        Load More
                    </Button>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddProductModal;
